import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  createRole,
  findAllRoles,
  findRole,
  updateRole,
} from "../../../API/apiRoles";
import {
  findRoleSwitchRules,
  saveRoleSwitchRules,
} from "../../../API/apiRoleSwitchRules";
import { SystemRole } from "../../../types/SystemRole";
import styles from "./GestaoPerfis.module.css";
import styles_grid from "../../../styles/Grid.module.css";
import styles_input from "../../../styles/Input.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_general from "../../../styles/General.module.css";
import styles_table from "../../../styles/Table.module.css";

const NovoPerfil = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleId = searchParams.get("id");
  const isEdit = Boolean(roleId);

  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [loadingRole, setLoadingRole] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);

  const [switchableRoles, setSwitchableRoles] = useState<SystemRole[]>([]);
  const [selectedTargetRoleIds, setSelectedTargetRoleIds] = useState<Set<number>>(new Set());
  const [loadingSwitchConfig, setLoadingSwitchConfig] = useState(false);
  const [loadingSwitchSave, setLoadingSwitchSave] = useState(false);
  const [msgSwitchSuccess, setMsgSwitchSuccess] = useState<string | null>(null);
  const [msgSwitchError, setMsgSwitchError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !roleId) return;
    loadRole(roleId);
    loadSwitchConfig(Number(roleId));
  }, [isEdit, roleId]);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  useEffect(() => {
    if (!msgSwitchSuccess) return;
    const timer = setTimeout(() => setMsgSwitchSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSwitchSuccess]);

  useEffect(() => {
    if (!msgSwitchError) return;
    const timer = setTimeout(() => setMsgSwitchError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSwitchError]);

  const loadRole = async (id: string) => {
    setLoadingRole(true);
    try {
      const response = await findRole(id);
      setNome(response.nome || "");
      setDescricao(response.descricao || "");
    } catch (error) {
      setMsgError("Não foi possível carregar o perfil.");
    } finally {
      setLoadingRole(false);
    }
  };

  const loadAllSwitchableRoles = async () => {
    const perPage = 100;
    let currentPage = 1;
    let lastPage = 1;
    const allRoles: SystemRole[] = [];

    do {
      const response = await findAllRoles(currentPage, perPage, 1);
      allRoles.push(...(response.roles || []));
      lastPage = response.pagination?.last_page || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return allRoles;
  };

  const loadSwitchConfig = async (sourceRoleId: number) => {
    setLoadingSwitchConfig(true);
    try {
      const [rolesResponse, switchRulesResponse] = await Promise.all([
        loadAllSwitchableRoles(),
        findRoleSwitchRules(sourceRoleId),
      ]);

      setSwitchableRoles(
        rolesResponse.filter((role) => role.id !== sourceRoleId),
      );

      setSelectedTargetRoleIds(new Set(switchRulesResponse.target_role_ids || []));
    } catch (error) {
      setMsgSwitchError("Não foi possível carregar as configurações de mudança de perfil.");
    } finally {
      setLoadingSwitchConfig(false);
    }
  };

  const handleToggleTargetRole = (targetRoleId: number) => {
    setSelectedTargetRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(targetRoleId)) {
        next.delete(targetRoleId);
      } else {
        next.add(targetRoleId);
      }
      return next;
    });
  };

  const handleSaveSwitchRules = async () => {
    if (!roleId) return;

    try {
      setLoadingSwitchSave(true);
      await saveRoleSwitchRules({
        source_role_id: Number(roleId),
        target_role_ids: Array.from(selectedTargetRoleIds),
      });
      setMsgSwitchSuccess("Configurações de mudança de perfil salvas com sucesso.");
    } catch (error) {
      setMsgSwitchError("Não foi possível salvar as configurações de mudança de perfil.");
    } finally {
      setLoadingSwitchSave(false);
    }
  };

  const handleSave = async () => {
    if (!nome.trim() || !descricao.trim()) {
      setMsgError("Preencha nome e descrição.");
      return;
    }

    try {
      setLoadingSave(true);
      const payload = {
        nome: nome.trim(),
        descricao: descricao.trim(),
      };

      if (isEdit && roleId) {
        await updateRole(roleId, payload);
        setMsgSuccess("Perfil atualizado com sucesso.");
      } else {
        await createRole(payload);
        setMsgSuccess("Perfil criado com sucesso.");
        setNome("");
        setDescricao("");
      }

      setTimeout(() => navigate("/perfis"), 700);
    } catch (error) {
      setMsgError("Não foi possível salvar o perfil.");
    } finally {
      setLoadingSave(false);
    }
  };

  return (
    <Card className={styles.card}>
      <CardContent>
        <div className={styles.title}>
          {isEdit ? "Editar Perfil" : "Novo Perfil"}
        </div>

        {msgSuccess && (
          <div className={styles_general.mt4}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}

        {msgError && (
          <div className={styles_general.mt4}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        {loadingRole ? (
          <div className={styles_general.mt6}>
            <CircularProgress />
          </div>
        ) : (
          <div className={`${styles_grid.grid12} ${styles_general.mt6}`}>
            <div className={styles_grid.inputGroup6}>
              <label className={styles_input.titleInput}>Nome</label>
              <TextField
                size="small"
                placeholder=""
                variant="standard"
                fullWidth
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                inputProps={{ maxLength: 120 }}
                InputProps={{
                  className: styles_input.input,
                  disableUnderline: true,
                }}
              />
            </div>

            <div className={styles_grid.inputGroup6}>
              <label className={styles_input.titleInput}>Descrição</label>
              <TextField
                size="small"
                placeholder=""
                variant="standard"
                fullWidth
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                inputProps={{ maxLength: 255 }}
                InputProps={{
                  className: styles_input.input,
                  disableUnderline: true,
                }}
              />
            </div>

            <div className={`${styles_grid.inputGroupFull} ${styles_grid.justifyEnd} ${styles_general.mt6}`}>
              <Button
                variant="contained"
                onClick={() => navigate("/perfis")}
                className={styles_button.buttonSquareSecondary}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                className={`${styles_button.buttonSquarePrimary} ${loadingSave ? styles_button.buttonOpacity : ""}`}
                disabled={loadingSave}
              >
                {loadingSave ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        )}

        {isEdit && (
          <div className={styles_general.mt8}>
            <div className={styles.sectionTitle}>Configuração de Mudança de Perfil</div>

            {msgSwitchSuccess && (
              <div className={styles_table.message}>
                <Alert severity="success">{msgSwitchSuccess}</Alert>
              </div>
            )}

            {msgSwitchError && (
              <div className={styles_table.message}>
                <Alert severity="error">{msgSwitchError}</Alert>
              </div>
            )}

            {loadingSwitchConfig ? (
              <div className={styles_general.mt6}>
                <CircularProgress />
              </div>
            ) : (
              <TableContainer className={styles_table.tableContainer}>
                <Table size="small">
                  <TableHead className={styles_table.tableHead}>
                    <TableRow>
                      <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Permitir</TableCell>
                      <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Perfil</TableCell>
                      <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Descrição</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {switchableRoles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" className={styles_table.textXs}>
                          Nenhum perfil disponível para mudança.
                        </TableCell>
                      </TableRow>
                    )}

                    {switchableRoles.map((role, index) => (
                      <TableRow
                        key={role.id}
                        className={`${styles_table.tableRow} ${index % 2 === 0 ? styles_table.tableRowEven : styles_table.tableRowOdd}`}
                      >
                        <TableCell padding="none" align="center">
                          <Checkbox
                            checked={selectedTargetRoleIds.has(role.id)}
                            onChange={() => handleToggleTargetRole(role.id)}
                          />
                        </TableCell>
                        <TableCell padding="none" className={styles_table.textXs}>
                          {role.nome}
                        </TableCell>
                        <TableCell padding="none" className={styles_table.textXs}>
                          {role.descricao}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <div className={`${styles_general.mt6} ${styles_grid.justifyEnd}`}>
              <Button
                variant="contained"
                className={`${styles_button.buttonSquarePrimary} ${loadingSwitchSave ? styles_button.buttonOpacity : ""}`}
                onClick={handleSaveSwitchRules}
                disabled={loadingSwitchSave}
              >
                {loadingSwitchSave ? "Salvando..." : "Salvar Mudanças de Perfil"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NovoPerfil;
